import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Play } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const empty = { name: "", image: "", link: "" };

export const PlatformsManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/platforms").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/platforms/${editing.id}`, form);
      else await api.post("/admin/platforms", form);
      toast.success("Plataforma salva"); setEditing(null); await load();
    } catch { toast.error("Erro ao salvar"); } finally { setSaving(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    await api.delete(`/admin/platforms/${p.id}`); toast.success("Excluída"); load();
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url })); toast.success("Imagem enviada");
    } catch { toast.error("Falha no upload"); } finally { setUploading(false); }
  };

  return (
    <div data-testid="admin-platforms">
      <PageTitle title="Lançamentos do momento" subtitle="Cartões em destaque com imagem, nome e botão Jogue"
        action={<GoldButton onClick={() => { setForm(empty); setEditing({}); }} data-testid="platform-add"><Plus className="w-4 h-4 inline mr-1" /> Adicionar</GoldButton>} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <Card key={p.id} className="!p-3" data-testid={`platform-admin-${p.id}`}>
            <div className="aspect-square rounded-xl overflow-hidden bg-white/5">
              {p.image ? <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">Sem imagem</div>}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-display font-bold text-white truncate">{p.name}</div>
                <div className="text-[11px] text-white/40 truncate">{p.link || "Sem link"}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm({ name: p.name, image: p.image, link: p.link }); setEditing(p); }} className="p-2 text-white/60 hover:text-[#F5C542]" data-testid={`platform-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p)} className="p-2 text-white/60 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card className="col-span-full text-center py-10 text-white/40">Nenhuma plataforma.</Card>}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setEditing(null)} />
            <motion.form onSubmit={save} initial={{ scale: 0.94 }} animate={{ scale: 1 }} exit={{ scale: 0.94 }} className="relative glass-gold w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="platform-modal">
              <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
              <h3 className="font-display text-xl font-bold text-white mb-5">{editing.id ? "Editar Plataforma" : "Nova Plataforma"}</h3>
              <div className="space-y-4">
                <Field label="Nome"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="platform-name" /></Field>
                <Field label="Link do botão Jogue"><input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." className={inputCls} data-testid="platform-link" /></Field>
                <Field label="Imagem">
                  <div className="flex gap-2">
                    <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} data-testid="platform-image" />
                    <label className="shrink-0 flex items-center gap-1.5 px-3 rounded-xl bg-white/[0.04] border border-white/10 text-white/70 cursor-pointer text-sm">
                      <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                    </label>
                  </div>
                </Field>
                {form.image && (
                  <div className="rounded-xl overflow-hidden aspect-square max-w-[180px] mx-auto border border-white/10">
                    <img src={imgUrl(form.image)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <GoldButton type="submit" disabled={saving} className="flex-1" data-testid="platform-save"><Play className="w-4 h-4 inline mr-1" fill="#120D0D" /> {saving ? "Salvando..." : "Salvar"}</GoldButton>
                <GhostButton type="button" onClick={() => setEditing(null)}>Cancelar</GhostButton>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
